import React from 'react'
import {Box, Typography} from '@mui/material'
import DOMPurify from 'dompurify'
import AppDialog from 'src/components-new/feedback/AppDialog'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import moment from 'moment'
import {CommunicationRow, renderNotificationType} from './communicationsColumns'

interface CommunicationDetailDialogNewProps {
  open: boolean
  onClose: () => void
  communication: CommunicationRow | undefined
}

/**
 * Read-only detail dialog — new-style equivalent of
 * src/components/templates/CommunicationDialog/CommunicationDialog.tsx. There is no
 * "actions" footer (only AppDialog's own close button), matching the legacy dialog which
 * only ever displays data. `ReadOnlyField` (src/components-new/inputs/ReadOnlyField) is used
 * for the plain text fields; the message body gets its own block below since it may need
 * sanitized HTML rendering (`ReadOnlyField` only supports plain-string values).
 */
export default function CommunicationDetailDialogNew({
  open,
  onClose,
  communication,
}: CommunicationDetailDialogNewProps): JSX.Element {
  const hasHtmlMessage = Boolean(communication?.message)

  return (
    <AppDialog
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={communication?.title || 'Messaggio'}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, pt: 1}}>
        <ReadOnlyField
          label="Data invio"
          value={communication?.sentDate ? moment(communication.sentDate).format('DD/MM/YYYY HH:mm') : undefined}
        />
        <ReadOnlyField label="Tipo" value={renderNotificationType(communication?.type)} />
        <ReadOnlyField label="Titolo" value={communication?.title} />

        <Box sx={{py: '10px'}}>
          <Typography
            sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}
            variant="caption"
          >
            Messaggio
          </Typography>
          {hasHtmlMessage ? (
            <Box
              // Sanitized via DOMPurify, same as the legacy CommunicationDialog.
              dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(communication!.message!)}}
              sx={{
                mt: 0.5,
                color: NEUTRAL_SCALE[900],
                fontSize: 14,
                wordBreak: 'break-word',
                maxWidth: '100%',
                // Email bodies often carry fixed-width tables (typical email-template markup),
                // which would otherwise push past the mobile bottom-sheet's edge — the sheet's
                // Paper has overflow:'hidden' (AppDialog.tsx), so anything wider than it gets
                // silently clipped rather than scrolled. Scope the fix to this block: scroll
                // horizontally if needed, and shrink the common overflow culprits to fit.
                overflowX: 'auto',
                '& table': {maxWidth: '100%', width: '100% !important'},
                '& img': {maxWidth: '100%', height: 'auto'},
              }}
            />
          ) : (
            <Typography sx={{mt: 0.5, color: communication?.plainTextMessage ? NEUTRAL_SCALE[900] : NEUTRAL_SCALE[500]}} variant="body2">
              {communication?.plainTextMessage || '—'}
            </Typography>
          )}
        </Box>
      </Box>
    </AppDialog>
  )
}
